import React from "react";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  Receipt as ReceiptIcon,
  DollarSign,
} from "lucide-react";

import {
  SalesAndPaymentsProvider,
  useSalesAndPayments,
} from "@/context/SalesAndPaymentsContext";
import SalesTab from "@/components/sales/SalesTab";
import DefaultedTab from "@/components/sales/DefaultedTab";
import PaymentsTab from "@/components/payments/PaymentsTab";
import SalesDialogs from "@/components/sales/SalesDialogs";

/**
 * Inner page component — consumes the SalesAndPayments context.
 */
function SalesAndPaymentsInner() {
  const { activeTab, setActiveTabAndUrl } = useSalesAndPayments();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-1">
          Sales & Payments Dashboard
        </h1>
        <p className="text-sm text-slate-600">
          Manage sales, payments, and track defaults
        </p>
      </div>

      {/* Main tab switcher */}
      <div className="flex gap-2 mb-8 border-b border-slate-200 pb-2">
        <Button
          variant={activeTab === "sales" ? "default" : "ghost"}
          className="gap-2"
          onClick={() => setActiveTabAndUrl("sales")}
        >
          <DollarSign className="w-4 h-4" /> Sales
        </Button>
        <Button
          variant={activeTab === "defaulted" ? "default" : "ghost"}
          className="gap-2"
          onClick={() => setActiveTabAndUrl("defaulted")}
        >
          <AlertTriangle className="w-4 h-4" /> Defaulted
        </Button>
        <Button
          variant={activeTab === "payments" ? "default" : "ghost"}
          className="gap-2"
          onClick={() => setActiveTabAndUrl("payments")}
        >
          <ReceiptIcon className="w-4 h-4" /> Payments
        </Button>
      </div>

      {/* Active tab content */}
      {activeTab === "sales" && <SalesTab />}
      {activeTab === "defaulted" && <DefaultedTab />}
      {activeTab === "payments" && <PaymentsTab />}

      {/* Dialogs (always mounted, visibility controlled internally) */}
      <SalesDialogs />
    </div>
  );
}

/**
 * Exported page component — wraps everything in the provider.
 */
export default function SalesAndPaymentsPage() {
  return (
    <SalesAndPaymentsProvider>
      <SalesAndPaymentsInner />
    </SalesAndPaymentsProvider>
  );
}